import { randomUInt64Number } from "@shared/common/utils/uint64";

type QueueItem<T> = {
  request: T;
  resolve: () => void;
};

export type InsightRequest = {
  url: string;
  requestType: "NORMAL" | "CHANGE_USER";
  formData: Record<string, string>;
};

const withTimeout = <T>(promise: Promise<T>, timeout: number) => {
  if (timeout <= 0) {
    return promise;
  }

  const timeoutError = new Promise<T>((_, reject) =>
    setTimeout(() => reject(new Error("timeout")), timeout)
  );

  return Promise.race([promise, timeoutError]);
};

/**
 * 큐를 기반으로 요청을 날리는 함수를 생성합니다.
 *
 * - `requestType`이 `CHANGE_USER`인 요청은 다른 요청이 모두 끝난 후에 하나씩 처리됩니다.
 * - `requestType`이 `NORMAL`인 요청은 `CHANGE_USER` 요청이 없을 때에만 동시 처리됩니다. 서로 다른 `NORMAL` 요청끼리는 병렬로 처리됩니다.
 */
export function createQueueBasedSend<
  T,
  Event extends { requestType: "NORMAL" | "CHANGE_USER" }
>(
  handleRequest: (event: Event) => Promise<T>,
  { timeout = 5000 }: { timeout?: number } = {}
): (request: Event) => Promise<T> {
  let isUserEventProcessing = false;

  const waitForUserEventQueue: QueueItem<Event>[] = [];

  const processingNormalEvents = new Map<string, Event>();

  const waitForNormalEventQueue: QueueItem<Event>[] = [];

  async function waitForUserEvent(request: Event) {
    if (isUserEventProcessing) {
      return new Promise<void>((resolve) => {
        waitForUserEventQueue.push({ resolve, request });
      });
    }
  }

  async function lockUserEvent(request: Event) {
    if (isUserEventProcessing) {
      return new Promise<void>((resolve) => {
        waitForUserEventQueue.push({ resolve, request });
      });
    } else {
      isUserEventProcessing = true;
    }
  }

  function unlockUserEvent() {
    let nextResolve = waitForUserEventQueue.shift();

    // 다음 일반 요청을 한꺼번에 처리하기 위해 배열로 담아두도록 함.
    const nextNormalResolves = [];
    let nextChangeUserResolve: QueueItem<Event> | undefined;
    while (nextResolve) {
      if (nextResolve.request.requestType === "NORMAL") {
        nextNormalResolves.push(nextResolve.resolve);
        nextResolve = waitForUserEventQueue.shift();
      } else {
        nextChangeUserResolve = nextResolve;
        break;
      }
    }

    // 일반 요청을 먼저 풀어줘야 함. (순서 중요)
    nextNormalResolves.forEach((resolve) => resolve());

    // 대기 중인 사용자 정보 변경 요청 처리
    if (nextChangeUserResolve) {
      nextChangeUserResolve.resolve();
    } else {
      isUserEventProcessing = false;
    }
  }

  function addNormalEvent(id: string, request: Event) {
    processingNormalEvents.set(id, request);
  }

  async function waitForNormalEvent(request: Event) {
    if (processingNormalEvents.size !== 0) {
      return new Promise<void>((resolve) => {
        waitForNormalEventQueue.push({ request, resolve });
      });
    }
  }

  function deleteNormalEvent(id: string) {
    processingNormalEvents.delete(id);
    if (processingNormalEvents.size === 0) {
      const userEventRequest = waitForNormalEventQueue.shift();
      userEventRequest?.resolve();
    }
  }

  const run = async (request: Event) => {
    // change user
    if (request.requestType === "CHANGE_USER") {
      await lockUserEvent(request);
      await waitForNormalEvent(request);
      try {
        return await withTimeout(handleRequest(request), timeout);
      } finally {
        unlockUserEvent();
      }
    }
    // normal
    const id = randomUInt64Number();
    await waitForUserEvent(request);
    addNormalEvent(id, request);
    try {
      return await withTimeout(handleRequest(request), timeout);
    } finally {
      deleteNormalEvent(id);
    }
  };

  return run;
}
